import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card } from './components/ui/Card';
import { Button } from './components/ui/Button';
import { Input } from './components/ui/Input';
import { GlobalStyles } from './styles/GlobalStyles';
import { toast, Toaster } from 'react-hot-toast';
import { Music, Search, Upload, Calendar, ExternalLink, Download, FileText, Clock, CheckCircle, XCircle } from 'lucide-react';

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SearchSection = styled.div`
  margin-bottom: 3rem;
`;

const SearchCard = styled(Card)`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  font-size: 1.1rem;
  padding: 0.75rem 1rem;
`;

const UploadSection = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 2rem;
`;

const UploadLabel = styled.label`
  display: inline-block;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 1rem;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled(Button)`
  width: 100%;
  margin-bottom: 1rem;
`;

const ProgressSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProgressTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d3748;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const ProgressStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4a5568;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: #2d3748;
`;

const ResultsSection = styled.div`
  margin-bottom: 2rem;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ResultCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  gap: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const AlbumArt = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    color: white;
    opacity: 0.8;
  }
`;

const TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrackTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ArtistName = styled.p`
  font-size: 0.9rem;
  color: #718096;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReleaseDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #4a5568;
  margin-bottom: 0.5rem;
`;

const UPCInfo = styled.div`
  font-size: 0.8rem;
  color: #a0aec0;
  margin-bottom: 0.75rem;
`;

interface MusicRelease {
  upc: string;
  title: string;
  artist: string;
  releaseDate: string;
  albumArt?: string;
  externalUrl?: string;
}

interface BatchStatus {
  batchId: string;
  total: number;
  processed: number;
  completed: boolean;
  results: Array<{
    upc: string;
    results: MusicRelease[];
    error?: string;
    releaseStatus: 'found' | 'not_found' | 'error';
  }>;
  summary: {
    found: number;
    notFound: number;
    errors: number;
  };
  timestamp: string;
}

function App() {
  const [upcInput, setUpcInput] = useState('');
  const [results, setResults] = useState<MusicRelease[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<BatchStatus | null>(null);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clean up polling on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  const handleSearch = async () => {
    if (!upcInput.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/lookup/upc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ upc: upcInput.trim() }),
      });

      const data = await response.json();
      
      if (response.ok && data.results) {
        setResults(data.results);
      } else {
        setResults([]);
        toast.error(data.error || 'No results found');
      }
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
      toast.error('Error searching for UPC');
    } finally {
      setLoading(false);
    }
  };

  const pollBatchStatus = async (batchId: string) => {
    try {
      const response = await fetch(`/api/lookup/batch/${batchId}/status`);
      const data = await response.json();
      
      if (response.ok) {
        setCurrentBatch(data);
        
        // Extract music releases from the batch results
        const musicReleases: MusicRelease[] = [];
        data.results.forEach((result: any) => {
          if (result.results && result.results.length > 0) {
            musicReleases.push(...result.results);
          }
        });
        setResults(musicReleases);
        
        // Stop polling if completed
        if (data.completed && pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
          toast.success(`Processing completed! Found ${data.summary.found} releases out of ${data.total} UPCs.`);
        }
      } else {
        console.error('Error polling batch status:', data.error);
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
        toast.error('Error checking batch status');
      }
    } catch (error) {
      console.error('Error polling batch status:', error);
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    }
  };

  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setCurrentBatch(null);
    setResults([]);
    
    try {
      const formData = new FormData();
      formData.append('csv', file);

      const response = await fetch('/api/lookup/csv', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (response.ok && data.batchId) {
        toast.success(`Processing started for ${data.total} UPCs`);
        
        // Start polling for batch status
        const interval = setInterval(() => {
          pollBatchStatus(data.batchId);
        }, 2000); // Poll every 2 seconds
        
        setPollInterval(interval);
        
        // Initial poll
        pollBatchStatus(data.batchId);
        
      } else {
        console.error('CSV processing error:', data.error || 'Failed to process CSV');
        toast.error(data.error || 'Failed to process CSV');
      }
    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error('Error processing CSV');
    } finally {
      setLoading(false);
      // Reset the input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleDownload = async () => {
    if (!currentBatch) return;
    
    try {
      const response = await fetch(`/api/lookup/batch/${currentBatch.batchId}/download`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `upc_lookup_results_${currentBatch.batchId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        toast.error('Failed to download results');
      }
    } catch (error) {
      console.error('Error downloading results:', error);
      toast.error('Error downloading results');
    }
  };

  return (
    <AppContainer>
      <GlobalStyles />
      <Toaster position="top-right" />
      
      <Header>
        <Title>Release Date Lookup</Title>
        <Subtitle>
          Discover music release dates from UPCs with our modern, fast search tool
        </Subtitle>
      </Header>

      <MainContent>
        <SearchSection>
          <SearchCard variant="elevated">
            <SearchForm onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
              <SearchInput
                type="text"
                placeholder="Enter UPC code..."
                value={upcInput}
                onChange={(e) => setUpcInput(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                leftIcon={<Search size={20} />}
                disabled={loading || !upcInput.trim()}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </SearchForm>

            <UploadSection>
              <UploadLabel htmlFor="csv-upload">
                <FileText size={20} style={{ marginRight: '0.5rem' }} />
                Batch Upload CSV
              </UploadLabel>
              <FileInput
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                disabled={loading}
              />
              <UploadButton
                variant="secondary"
                leftIcon={<Upload size={20} />}
                disabled={loading}
                onClick={() => document.getElementById('csv-upload')?.click()}
              >
                {loading ? 'Processing...' : 'Upload CSV File'}
              </UploadButton>
            </UploadSection>
          </SearchCard>
        </SearchSection>

        {currentBatch && (
          <ProgressSection>
            <ProgressHeader>
              <ProgressTitle>
                <Clock size={20} style={{ marginRight: '0.5rem' }} />
                Processing Progress
              </ProgressTitle>
              {currentBatch.completed && (
                <Button
                  variant="primary"
                  size="small"
                  leftIcon={<Download size={16} />}
                  onClick={handleDownload}
                >
                  Download Results
                </Button>
              )}
            </ProgressHeader>
            
            <ProgressBar>
              <ProgressFill percentage={(currentBatch.processed / currentBatch.total) * 100} />
            </ProgressBar>
            
            <ProgressStats>
              <StatItem>
                <FileText size={16} />
                Total: <StatValue>{currentBatch.total}</StatValue>
              </StatItem>
              <StatItem>
                <Clock size={16} />
                Processed: <StatValue>{currentBatch.processed}</StatValue>
              </StatItem>
              <StatItem>
                <CheckCircle size={16} />
                Found: <StatValue>{currentBatch.summary.found}</StatValue>
              </StatItem>
              <StatItem>
                <XCircle size={16} />
                Not Found: <StatValue>{currentBatch.summary.notFound}</StatValue>
              </StatItem>
              <StatItem>
                <XCircle size={16} />
                Errors: <StatValue>{currentBatch.summary.errors}</StatValue>
              </StatItem>
            </ProgressStats>
            
            <p style={{ fontSize: '0.9rem', color: '#718096', margin: 0 }}>
              {currentBatch.completed ? 'Processing completed!' : 'Processing in progress...'}
            </p>
          </ProgressSection>
        )}

        {results.length > 0 && (
          <ResultsSection>
            <ResultsHeader>
              <h2>Results</h2>
              {currentBatch && !currentBatch.completed && (
                <p style={{ fontSize: '0.9rem', color: '#718096', margin: 0 }}>
                  Showing latest {Math.min(results.length, 50)} results
                </p>
              )}
            </ResultsHeader>
            <ResultsGrid>
              {results.map((result, index) => (
                <ResultCard key={index} variant="elevated">
                  <AlbumArt>
                    {result.albumArt ? (
                      <img src={result.albumArt} alt={result.title} />
                    ) : (
                      <Music size={48} />
                    )}
                  </AlbumArt>
                  <TrackInfo>
                    <TrackTitle>{result.title}</TrackTitle>
                    <ArtistName>{result.artist}</ArtistName>
                    <ReleaseDate>
                      <Calendar size={16} />
                      {result.releaseDate}
                    </ReleaseDate>
                    <UPCInfo>UPC: {result.upc}</UPCInfo>
                    {result.externalUrl && (
                      <Button
                        variant="ghost"
                        size="small"
                        leftIcon={<ExternalLink size={16} />}
                        onClick={() => window.open(result.externalUrl, '_blank')}
                      >
                        View on Platform
                      </Button>
                    )}
                  </TrackInfo>
                </ResultCard>
              ))}
            </ResultsGrid>
          </ResultsSection>
        )}
      </MainContent>
    </AppContainer>
  );
}

export default App; 